import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  message,
  Select,
  Card,
  Button,
  DatePicker,
  Space,
  Tag,
} from "antd";
import axios from "axios";
import moment from "moment-timezone";
import PageBreadcrumb from "../../component/PageBreadcrumb";

const { RangePicker } = DatePicker;
const API_BASE = "https://utils.clapingo.com/api/report";

const formatDate = (date) =>
  moment(date).tz("Asia/Kolkata").format("YYYY-MM-DD");

const getMonthColor = (month) => {
    if (month <= 1) return 'green';
     if (month <= 3) return 'blue';
    if (month <= 6) return 'yellow';
    return 'orange';
}

/** ====================== 
 *  API SERVICE LAYER
 *  ====================== */
const fetchSubscriptionReminders = async ({ 
  page,
  limit,
  rangeType,
  startDate,
  endDate,
}) => {
  const params = new URLSearchParams({ page, limit });

  if (rangeType === "custom" && startDate && endDate) {
    params.append("startDate", startDate);
    params.append("endDate", endDate);
  } else {
    params.append("range", rangeType);
  }

  const { data } = await axios.get(
    `${API_BASE}/subscriptionReminderDataSummary?${params.toString()}`
  );
  return data;
};

/** ====================== 
 *  CSV BUILDER
 *  ====================== */
const buildCsv = (items) => {
  const headers = [
    "Learner Name",
    "Phone Number",
    "Email",
    "Subscription Type",
    "Status",
    "Month",
    "Has Upcoming",
    "Start Date",
    "End Date",
    "Total Sessions",
    "Available Sessions",
  ];

  const escape = (val) => {
    if (val == null) return "";
    const str = String(val);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const rows = items.map((item) => [
    escape(item.leanerDetails?.[0]?.name),
    escape(item.leanerDetails?.[0]?.phoneNumber),
    escape(item.leanerDetails?.[0]?.email),
    escape(item.subscriptionType),
    escape(item.status),
    escape(item.month),
    escape(item.hasUpcoming ? "Yes" : "No"),
    escape(formatDate(item.startDate)),
    escape(formatDate(item.endDate)),
    escape(item.totalSession),
    escape(item.availableSession),
  ]);

  return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
};

/** ====================== 
 *  MAIN COMPONENT
 *  ====================== */
const SubscriptionReminder = () => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [pagination, setPagination] = useState({
    totalDocs: 0,
    currentPage: 1,
    limit: 20,
  });
  const [dateRange, setDateRange] = useState(null);
  const [rangeType, setRangeType] = useState("daily");

  /** ====================== 
   *  TABLE COLUMNS
   *  ====================== */
  const columns = useMemo(
    () => [
      {
        title: "Learner Name",
        dataIndex: "leanerDetails",
        key: "name",
        render: (d) => d?.[0]?.name,
      },
      {
        title: "Phone Number",
        dataIndex: "leanerDetails",
        key: "phoneNumber",
        render: (d) => d?.[0]?.phoneNumber,
      },
      {
        title: "Email",
        dataIndex: "leanerDetails",
        key: "email",
        render: (d) => d?.[0]?.email,
      },
      { title: "Subscription Type", dataIndex: "subscriptionType" },
      { title: "Status", dataIndex: "status" },
      {
        title: "Month",
        dataIndex: "month",
        key: "month",
        render: (month) => (
          <Tag color={getMonthColor(month)}>{month}</Tag>
        ),
      },
      {
        title: "Has Upcoming",
        dataIndex: "hasUpcoming",
        render: (val) => (
          <Tag color={val ? "green" : "red"}>{val ? "Yes" : "No"}</Tag>
        ),
      },
      {
        title: "Start Date",
        dataIndex: "startDate",
        render: (date) => formatDate(date),
      },
      {
        title: "End Date",
        dataIndex: "endDate",
        render: (date) => formatDate(date),
      },
      { title: "Total Sessions", dataIndex: "totalSession" },
      { title: "Available Sessions", dataIndex: "availableSession" },
    ],
    []
  );

  /** ====================== 
   *  DATA FETCH
   *  ====================== */
  const fetchReminders = useCallback(
    async (page = 1, limit = pagination.limit) => {
      const isCustom = rangeType === "custom";
      const startDate = isCustom ? dateRange?.[0]?.format("YYYY-MM-DD") : null;
      const endDate = isCustom ? dateRange?.[1]?.format("YYYY-MM-DD") : null;

      if (isCustom && (!startDate || !endDate)) {
        message.error("Please select a valid date range");
        return;
      }

      try {
        setLoading(true);
        const data = await fetchSubscriptionReminders({
          page,
          limit,
          rangeType,
          startDate,
          endDate,
        });
        setReminders(data.data || []);
        setPagination({
          totalDocs: data.pagination.totalDocs,
          currentPage: data.pagination.currentPage,
          limit: data.pagination.limit,
        });
      } catch (err) {
        console.error(err);
        message.error("Failed to fetch subscription reminders");
      } finally {
        setLoading(false);
      }
    },
    [rangeType, dateRange, pagination.limit]
  );

  /** ====================== 
   *  CSV DOWNLOAD
   *  ====================== */
  const downloadCsv = useCallback(async () => {
    const isCustom = rangeType === "custom";
    const start = isCustom ? dateRange?.[0]?.format("YYYY-MM-DD") : null;
    const end = isCustom ? dateRange?.[1]?.format("YYYY-MM-DD") : null;

    if (isCustom && (!start || !end)) {
      message.error("Please select a date range");
      return;
    }

    setDownloading(true);
    try {
      let page = 1;
      const limit = 200;
      let allItems = [];
      let totalPages = 1;

      do {
        const chunk = await fetchSubscriptionReminders({
          page,
          limit,
          rangeType,
          startDate: start,
          endDate: end,
        });
        totalPages = chunk?.pagination?.totalPages || 1;
        allItems = allItems.concat(chunk?.data || []);
        page += 1;
      } while (page <= totalPages);

      const csv = buildCsv(allItems);
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = isCustom ? `${start}_to_${end}` : rangeType;
      a.href = url;
      a.download = `subscription-reminders_${suffix}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      message.error("Failed to download CSV");
    } finally {
      setDownloading(false);
    }
  }, [rangeType, dateRange]);

  /** ====================== 
   *  INIT FETCH
   *  ====================== */
  useEffect(() => {
    fetchReminders(1, pagination.limit);
  }, [fetchReminders, pagination.limit]);

  /** ====================== 
   *  RENDER
   *  ====================== */
  return (
    <div>
      <PageBreadcrumb route={["Subscribed", "Subscription Reminder"]} />
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            value={rangeType}
            onChange={setRangeType}
            style={{ width: 160 }}
          >
            <Select.Option value="custom">Custom</Select.Option>
            <Select.Option value="daily">Daily</Select.Option>
            <Select.Option value="weekly">Weekly</Select.Option>
            <Select.Option value="monthly">Monthly</Select.Option>
          </Select>

          {rangeType === "custom" && (
            <>
              <span>Date range:</span>
              <RangePicker
                value={dateRange}
                onChange={(vals) => setDateRange(vals)}
                allowClear={false}
              />
            </>
          )}

          <Button onClick={() => fetchReminders(1, pagination.limit)} disabled={loading}>
            Fetch
          </Button>
          <Button
            type="primary"
            onClick={downloadCsv}
            loading={downloading}
            disabled={loading}
          >
            Download CSV
          </Button>
        </Space>
      </Card>

      <Table
        loading={loading}
        columns={columns}
        dataSource={reminders}
        rowKey="_id"
        pagination={{
          total: pagination.totalDocs,
          current: pagination.currentPage,
          pageSize: pagination.limit,
          showSizeChanger: true,
          onChange: fetchReminders,
          showTotal: (t) => `${t} items`,
        }}
        scroll={{ x: 1300 }}
      />
    </div>
  );
};

export default SubscriptionReminder;